// ── Mapbox layer initialization ──────────────────────────────────────────────
// Pure setup functions that add sources/layers to a Mapbox map instance.
// No React dependencies — called once from map.on("load").

import { buildRadarSitesGeoJson, buildNetworkColorExpr, setRadarSitesVisibility } from "../data/radar-sites";

// Map style IDs that use a dark basemap — wind barbs are drawn white on these.
export const DARK_MAP_STYLE_IDS = new Set(["dark", "satellite", "satellite-streets", "nav-night"]);

// ── Shared constants ────────────────────────────────────────────────────────

// Transparent 1×1 PNG used to initialise both radar sources before any frame arrives.
// Blob URL instead of data URI — Mapbox GL reliably decodes blob: URLs but can
// reject data: URIs in image sources with "source image could not be decoded".
const _TRANSPARENT_BYTES = Uint8Array.from(
  atob("iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQAABjE+ibYAAAAASUVORK5CYII="),
  (c) => c.charCodeAt(0),
);
export const TRANSPARENT_1PX = URL.createObjectURL(
  new Blob([_TRANSPARENT_BYTES], { type: "image/png" }),
);

// Placeholder coordinates — full-world bounds so the invisible initial image
// doesn't trigger out-of-range errors in Mapbox.
export const PLACEHOLDER_COORDS = [[-180, 90], [180, 90], [180, -90], [-180, -90]];

export const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };

// ── Warning polygon style expressions ───────────────────────────────────────

const WARN_COLOR_EXPR = [
  "match", ["get", "hazard_type"],
  "tornado", "#f43f5e",
  "severe",  "#f59e0b",
  "flood",   "#3b82f6",
  /* default */ "#94a3b8",
];

const WARN_FILL_OPACITY_EXPR = [
  "match", ["get", "hazard_type"],
  "tornado", 0.12,
  "severe",  0.08,
  "flood",   0.08,
  /* default */ 0.06,
];

const WARN_OUTLINE_WIDTH_EXPR = [
  "match", ["get", "hazard_type"],
  "tornado", 2.5,
  "severe",  2.0,
  "flood",   2.0,
  /* default */ 1.5,
];

// ── Warning polygon layers ──────────────────────────────────────────────────
// Inserted BEFORE "measure-line-glow" so warnings render:
//   above radar rasters, below measure tool and radar site markers.

export function initWarningLayers(map) {
  if (!map.getSource("warnings-source")) {
    // promoteId required for feature-state with GeoJSON sources.
    map.addSource("warnings-source", {
      type:      "geojson",
      data:      EMPTY_GEOJSON,
      promoteId: "id",
    });
  }
  if (!map.getLayer("warnings-fill")) {
    map.addLayer(
      {
        id:     "warnings-fill",
        type:   "fill",
        source: "warnings-source",
        paint: {
          "fill-color":     WARN_COLOR_EXPR,
          // Increase opacity slightly on hover via feature-state.
          "fill-opacity": [
            "case", ["boolean", ["feature-state", "hover"], false],
            ["*", WARN_FILL_OPACITY_EXPR, 2.2],
            WARN_FILL_OPACITY_EXPR,
          ],
          "fill-antialias": true,
        },
      },
      "measure-line-glow",
    );
  }
  if (!map.getLayer("warnings-outline")) {
    map.addLayer(
      {
        id:     "warnings-outline",
        type:   "line",
        source: "warnings-source",
        paint: {
          "line-color":   WARN_COLOR_EXPR,
          // Widen and brighten outline on hover.
          "line-width": [
            "case", ["boolean", ["feature-state", "hover"], false],
            ["*", WARN_OUTLINE_WIDTH_EXPR, 1.6],
            WARN_OUTLINE_WIDTH_EXPR,
          ],
          "line-opacity": [
            "case", ["boolean", ["feature-state", "hover"], false],
            1.0,
            0.85,
          ],
        },
      },
      "measure-line-glow",
    );
  }
  if (!map.getLayer("warnings-highlight")) {
    map.addLayer(
      {
        id:     "warnings-highlight",
        type:   "line",
        source: "warnings-source",
        // Nothing selected initially — filter matches no feature.
        filter: ["==", ["get", "id"], ""],
        paint: {
          "line-color":     "#ffffff",
          "line-width":     3,
          "line-opacity":   0.9,
          "line-dasharray": [3, 2],
        },
      },
      "measure-line-glow",
    );
  }
}

// ── Dual-buffer radar layers ────────────────────────────────────────────────
// Both slots start at opacity 0; the crossfade effect drives opacity from here on.
// Image sources (legacy fallback) + raster-tile sources (tile_url_template).

/**
 * Find the first base-style layer that should render ABOVE radar data.
 * Searches by source-layer name rather than style-specific layer IDs so it
 * works across dark-v11, light-v11, streets-v12, satellite-streets-v12, etc.
 * The first non-tunnel road layer marks where roads begin — everything above
 * it (road lines, road labels, city names, POIs) will paint on top of radar.
 * Falls back to the first symbol layer (labels only), then undefined
 * (adds on top — correct for satellite-only and Standard styles).
 */
export function getAboveRadarAnchor(map) {
  const layers = map.getStyle().layers;
  // Prefer inserting before the first surface road layer (source-layer "road",
  // excluding tunnel layers) so road lines AND labels all render above radar.
  const roadLayer = layers.find(
    (l) => l["source-layer"] === "road" && !l.id.toLowerCase().includes("tunnel")
  );
  if (roadLayer) return roadLayer.id;
  // Fallback: labels-only anchor (first symbol layer). Covers Standard style
  // and any style where road source-layer names differ.
  return layers.find((l) => l.type === "symbol")?.id;
}

/**
 * Moves all radar layers to just below the first road layer using moveLayer.
 * Call this AFTER all layers are initialized (end of style.load) so the full
 * layer stack is visible and moveLayer can reorder correctly.
 *
 * Uses multiple heuristics so it works across dark-v11, streets-v12, etc.:
 *   1. source-layer === "road" (mapbox-streets-v8)
 *   2. Layer ID starts with "road-" (common prefix in classic styles)
 *   3. First symbol layer (labels only — last resort)
 *
 * Desired render order (bottom → top): tiles-a, layer-a, tiles-b, layer-b,
 * then road/label layers above. Calling moveLayer(id, anchor) in this order
 * achieves that because each call inserts just before anchor, pushing prior
 * layers one slot further back.
 */
export function repositionRadarBelowRoads(map) {
  const layers = map.getStyle()?.layers;
  if (!layers?.length) return;

  const anchor =
    layers.find((l) => l["source-layer"] === "road" && !l.id.includes("tunnel"))?.id ||
    layers.find((l) => l.id.startsWith("road-"))?.id ||
    layers.find((l) => l.type === "symbol")?.id;

  if (!anchor) return;

  ["radar-tiles-a", "radar-layer-a", "radar-tiles-b", "radar-layer-b"].forEach((id) => {
    if (map.getLayer(id)) {
      try { map.moveLayer(id, anchor); } catch {}
    }
  });
}

export function initRadarLayers(map) {
  const anchor = getAboveRadarAnchor(map);
  ["a", "b"].forEach((slot) => {
    // Image source (existing — used when tile_url_template is absent)
    map.addSource(`radar-frame-${slot}`, {
      type:        "image",
      url:         TRANSPARENT_1PX,
      coordinates: PLACEHOLDER_COORDS,
    });
    map.addLayer({
      id:     `radar-layer-${slot}`,
      type:   "raster",
      source: `radar-frame-${slot}`,
      paint: {
        "raster-opacity":            0,
        // 180 ms crossfade — Mapbox's raster-opacity-transition is the WebGL
        // equivalent of a CSS opacity transition on position:absolute layers.
        "raster-opacity-transition": { duration: 180, delay: 0 },
        "raster-fade-duration":      0,
        "raster-resampling":         "linear",
        "raster-brightness-min":     0,
        "raster-contrast":           0.10,
        "raster-saturation":         0.05,
      },
    }, anchor);

    // Tile source (radar-tiles-${slot}) is NOT created here.
    // setTileSourceUrl() creates it on first real frame with the correct
    // tile URL and zoom range [4, 8].  Creating a placeholder here with
    // minzoom/maxzoom=25 caused Mapbox to request z=25 tiles from the backend
    // (clamped from any real zoom), producing HTTP 400 errors in the network tab.
  });
}

// ── Zoom-reactive resampling ─────────────────────────────────────────────────
// raster-resampling does not support Mapbox expressions; switch via
// setPaintProperty on every zoom event.
// linear ≤ TILE_MAXZOOM: tiles render at native resolution, bilinear is fine.
// nearest > TILE_MAXZOOM: tiles are overzoomed, nearest keeps edges crisp.

export const TILE_MAXZOOM = 8; // matches backend max zoom level

const RADAR_LAYER_IDS = [
  "radar-layer-a", "radar-layer-b",
  "radar-tile-layer-a", "radar-tile-layer-b",
];

export function syncRadarResampling(map) {
  const mode = map.getZoom() <= TILE_MAXZOOM ? "linear" : "nearest";
  RADAR_LAYER_IDS.forEach(id => {
    if (map.getLayer(id)) try { map.setPaintProperty(id, "raster-resampling", mode); } catch {}
  });
}

/**
 * Update a raster-tile source's tile URL.  Uses setTiles() when the source
 * already exists (fast path); falls back to remove+re-add on first call.
 */
export function setTileSourceUrl(map, slot, tileUrl, zoomRange) {
  const sourceId = `radar-tiles-${slot}`;
  const layerId  = `radar-tile-layer-${slot}`;

  // Fast path: update tile URL in-place if source already exists.
  const existing = map.getSource(sourceId);
  if (existing && typeof existing.setTiles === 'function') {
    existing.setTiles([tileUrl]);
    return;
  }

  // Slow path: first call for this slot — full creation.
  let opacity = 0;
  try { opacity = map.getPaintProperty(layerId, "raster-opacity") ?? 0; } catch {}

  if (map.getLayer(layerId))   map.removeLayer(layerId);
  if (map.getSource(sourceId))  map.removeSource(sourceId);

  const [minz, maxz] = zoomRange || [4, 8];
  map.addSource(sourceId, {
    type:     "raster",
    tiles:    [tileUrl],
    tileSize: 512,
    minzoom:  minz,
    maxzoom:  maxz,
  });

  // Re-add layer BEFORE the image-source layer so tile layers sit behind
  // image layers in the paint order.  If the image layer is hidden (opacity 0)
  // the tile layer shows through; if both are at 0 nothing is visible.
  const beforeLayer = `radar-layer-${slot}`;
  map.addLayer({
    id:     layerId,
    type:   "raster",
    source: sourceId,
    paint: {
      "raster-opacity":            opacity,
      "raster-opacity-transition": { duration: 180, delay: 0 },
      "raster-fade-duration":      0,
      "raster-resampling":         "linear",
      "raster-brightness-min":     0,
      "raster-contrast":           0.05,
      "raster-saturation":         0,
    },
  }, beforeLayer);
}

// ── Measure-tool layers ─────────────────────────────────────────────────────

export function resetMeasureLayers(map) {
  map.getSource("measure-pts")?.setData(EMPTY_GEOJSON);
  map.getSource("measure-ln")?.setData(EMPTY_GEOJSON);
}

export function initMeasureLayers(map) {
  if (!map.getSource("measure-pts")) {
    map.addSource("measure-pts", { type: "geojson", data: EMPTY_GEOJSON });
  }
  if (!map.getSource("measure-ln")) {
    map.addSource("measure-ln", { type: "geojson", data: EMPTY_GEOJSON });
  }
  map.addLayer({
    id: "measure-line-glow", type: "line", source: "measure-ln",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#38bdf8", "line-width": 8, "line-opacity": 0.12 },
  });
  map.addLayer({
    id: "measure-line-layer", type: "line", source: "measure-ln",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#38bdf8", "line-width": 2.5,
      "line-dasharray": [2, 1.5], "line-opacity": 0.95,
    },
  });
  map.addLayer({
    id: "measure-points-layer", type: "circle", source: "measure-pts",
    paint: {
      "circle-radius": 5, "circle-color": "#e2e8f0",
      "circle-stroke-color": "#38bdf8", "circle-stroke-width": 2, "circle-opacity": 0.95,
    },
  });
}

// ── Radar site layers ───────────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
export function refreshRadarSitesSource(map) {
  if (!map) return;
  const src = map.getSource("radar-sites-source");
  if (!src || typeof src.setData !== "function") return;
  src.setData(buildRadarSitesGeoJson());
}

export function initRadarSitesLayers(map, visible = true) {
  // Network color is the fallback for sites not covered by the health endpoint
  // (hasData: false catalog-only sites that are never polled by the backend).
  const networkColorExpr = buildNetworkColorExpr("#38bdf8");

  // Health overrides color for watched (hasData: true) sites.
  // "unknown" falls through to the network color.
  const healthColorExpr = [
    "match", ["get", "health"],
    "online",  "#22c55e",   // green
    "stale",   "#eab308",   // yellow
    "offline", "#ef4444",   // red
    networkColorExpr,        // fallback: catalog-only / not yet polled
  ];

  // Register pill SDF image once — white rounded-rect, colorable via icon-color
  if (!map.hasImage("radar-site-pill")) {
    const W = 48, H = 14, R = 7;
    const pc = document.createElement("canvas");
    pc.width = W; pc.height = H;
    const px = pc.getContext("2d");
    px.fillStyle = "#ffffff";
    px.beginPath();
    px.moveTo(R, 0);
    px.arcTo(W, 0, W, H, R);
    px.arcTo(W, H, 0, H, R);
    px.arcTo(0, H, 0, 0, R);
    px.arcTo(0, 0, W, 0, R);
    px.closePath();
    px.fill();
    map.addImage("radar-site-pill", px.getImageData(0, 0, W, H), {
      sdf: true,
      stretchX: [[R + 1, W - R - 1]],
      stretchY: [[H / 2 - 1, H / 2 + 1]],
    });
  }

  if (!map.getSource("radar-sites-source")) {
    map.addSource("radar-sites-source", {
      type: "geojson",
      data: buildRadarSitesGeoJson(),
    });
  }

  // Invisible circles kept solely as click / hover hit-targets (panel wires events to these IDs)
  if (!map.getLayer("radar-sites-halo")) {
    map.addLayer({
      id: "radar-sites-halo", type: "circle", source: "radar-sites-source",
      paint: { "circle-radius": 22, "circle-opacity": 0, "circle-stroke-width": 0 },
    });
  }
  if (!map.getLayer("radar-sites-layer")) {
    map.addLayer({
      id: "radar-sites-layer", type: "circle", source: "radar-sites-source",
      paint: { "circle-radius": 22, "circle-opacity": 0, "circle-stroke-width": 0 },
    });
  }

  // Pill label: icon-text-fit stretches the SDF pill to wrap the ICAO text
  if (!map.getLayer("radar-sites-label-layer")) {
    map.addLayer({
      id: "radar-sites-label-layer", type: "symbol", source: "radar-sites-source",
      minzoom: 4,
      layout: {
        "icon-image":            "radar-site-pill",
        "icon-text-fit":         "both",
        "icon-text-fit-padding": [2, 5, 2, 5],
        "text-field":  ["get", "id"],
        "text-size": [
          "interpolate", ["linear"], ["zoom"],
          4,  ["match", ["get", "hasData"], 1, 7, 6],
          8,  ["match", ["get", "hasData"], 1, 8, 7],
          14, ["match", ["get", "hasData"], 1, 9, 8],
        ],
        "text-anchor":         "center",
        "text-font":           ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
        "icon-allow-overlap":  true,
        "text-allow-overlap":  true,
      },
      paint: {
        "icon-color":      healthColorExpr,
        "icon-opacity":    ["match", ["get", "hasData"], 1, 0.92, 0.40],
        "icon-halo-color": "rgba(8, 11, 17, 0.80)",
        "icon-halo-width": 1.5,
        "text-color":      "#020617",
        "text-opacity":    ["match", ["get", "hasData"], 1, 0.95, 0.60],
      },
    });
  }

  setRadarSitesVisibility(map, visible);
}

// ── Surface fronts overlay ──────────────────────────────────────────────────
// Renders WPC National Forecast Chart fronts (Day 1, Layer 2) as colored
// lines above radar raster but below warnings and site markers.
// Property field: "feat" with values like "Cold Front Valid", "Warm Front Valid", etc.

// ── Front icon generation (Canvas) ──────────────────────────────────────────
// Programmatically create 32×32 triangle and semicircle icons for use with
// Mapbox symbol-placement: "line".  No external asset files needed.

const _ICON_SIZE = 32;

function _createTriangleIcon(color) {
  const s = _ICON_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(s / 2, 1);        // apex (top-center)
  ctx.lineTo(s - 2, s - 1);    // bottom-right
  ctx.lineTo(2, s - 1);        // bottom-left
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, s, s);
}

function _createSemicircleIcon(color) {
  const s = _ICON_SIZE;
  const r = s / 2 - 1;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = color;
  ctx.beginPath();
  // Semicircle: flat edge at bottom (y = s/2 + r), arc bulges upward
  ctx.arc(s / 2, s / 2 + 2, r, Math.PI, 0);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, s, s);
}

function _ensureFrontsIcons(map) {
  const icons = [
    ["front-cold",       () => _createTriangleIcon("#3b82f6")],
    ["front-warm",       () => _createSemicircleIcon("#ef4444")],
    ["front-occluded",   () => _createTriangleIcon("#7c3aed")],
    ["front-stationary", () => _createSemicircleIcon("#a855f7")],
  ];
  for (const [id, create] of icons) {
    if (!map.hasImage(id)) {
      map.addImage(id, create(), { pixelRatio: 2 });
    }
  }
}

// ── Front color + filter expressions ────────────────────────────────────────

const FRONT_COLOR_EXPR = [
  "match", ["get", "feat"],
  "Cold Front Valid",       "#3b82f6",   // blue
  "Cold Front Forecast",    "#3b82f6",
  "Warm Front Valid",       "#ef4444",   // red
  "Warm Front Forecast",    "#ef4444",
  "Stationary Front Valid", "#a855f7",   // purple
  "Stationary Front Forecast", "#a855f7",
  "Occluded Front Valid",   "#7c3aed",   // dark purple
  "Occluded Front Forecast","#7c3aed",
  "Trough Valid",           "#f59e0b",   // amber
  "Trough Forecast",        "#f59e0b",
  "Dryline Valid",          "#d97706",   // dark amber
  "Dryline Forecast",       "#d97706",
  "Squall Line Valid",      "#f43f5e",   // pink
  "Squall Line Forecast",   "#f43f5e",
  "Outflow Boundary Valid", "#6b7280",   // gray
  "Outflow Boundary Forecast","#6b7280",
  /* default */ "#94a3b8",
];

// Mapbox GL does NOT support data-driven line-dasharray, so we split into
// two layers: solid (major fronts) and dashed (boundaries/troughs).
// Use case-insensitive substring matching isn't available, so list both Valid/Forecast variants.
const FRONT_SOLID_FILTER = ["in", ["get", "feat"], ["literal", [
  "Cold Front Valid", "Cold Front Forecast",
  "Warm Front Valid", "Warm Front Forecast",
  "Stationary Front Valid", "Stationary Front Forecast",
  "Occluded Front Valid", "Occluded Front Forecast",
]]];
const FRONT_DASHED_FILTER = ["in", ["get", "feat"], ["literal", [
  "Trough Valid", "Trough Forecast",
  "Dryline Valid", "Dryline Forecast",
  "Squall Line Valid", "Squall Line Forecast",
  "Outflow Boundary Valid", "Outflow Boundary Forecast",
]]];

export function initFrontsLayers(map) {
  if (!map.getSource("fronts-source")) {
    map.addSource("fronts-source", {
      type: "geojson",
      data: EMPTY_GEOJSON,
    });
  }
  // Safe beforeId — right map may not have warnings layers.
  const beforeId = map.getLayer("warnings-fill") ? "warnings-fill"
                 : map.getLayer("radar-sites-halo") ? "radar-sites-halo"
                 : undefined;
  if (!map.getLayer("fronts-glow")) {
    map.addLayer({
      id: "fronts-glow", type: "line", source: "fronts-source",
      paint: {
        "line-color":   FRONT_COLOR_EXPR,
        "line-width":   6,
        "line-opacity": 0.15,
        "line-blur":    3,
      },
    }, beforeId);
  }
  // Solid layer: cold, warm, stationary, occluded fronts
  if (!map.getLayer("fronts-line-solid")) {
    map.addLayer({
      id: "fronts-line-solid", type: "line", source: "fronts-source",
      filter: FRONT_SOLID_FILTER,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color":   FRONT_COLOR_EXPR,
        "line-width":   2.5,
        "line-opacity": 0.85,
      },
    }, beforeId);
  }
  // Dashed layer: trough, dry line, squall line, outflow boundary
  if (!map.getLayer("fronts-line-dashed")) {
    map.addLayer({
      id: "fronts-line-dashed", type: "line", source: "fronts-source",
      filter: FRONT_DASHED_FILTER,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color":     FRONT_COLOR_EXPR,
        "line-width":     2,
        "line-opacity":   0.8,
        "line-dasharray": [5, 3],
      },
    }, beforeId);
  }
  // Symbol layer: meteorological symbols (triangles, semicircles) along lines
  _ensureFrontsIcons(map);
  if (!map.getLayer("fronts-symbols")) {
    map.addLayer({
      id: "fronts-symbols", type: "symbol", source: "fronts-source",
      filter: FRONT_SOLID_FILTER,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing":   40,
        "icon-image": [
          "match", ["get", "feat"],
          "Cold Front Valid",           "front-cold",
          "Cold Front Forecast",        "front-cold",
          "Warm Front Valid",           "front-warm",
          "Warm Front Forecast",        "front-warm",
          "Occluded Front Valid",       "front-occluded",
          "Occluded Front Forecast",    "front-occluded",
          "Stationary Front Valid",     "front-stationary",
          "Stationary Front Forecast",  "front-stationary",
          "",
        ],
        "icon-size":                0.55,
        "icon-allow-overlap":       true,
        "icon-rotation-alignment":  "map",
        "icon-pitch-alignment":     "map",
      },
      paint: {
        "icon-opacity": 0.9,
      },
    }, beforeId);
  }
  // Pressure center labels: H (High) and L (Low) on point features
  if (!map.getLayer("fronts-pressure-labels")) {
    map.addLayer({
      id: "fronts-pressure-labels", type: "symbol", source: "fronts-source",
      filter: ["==", ["geometry-type"], "Point"],
      layout: {
        "text-field": [
          "match", ["get", "feat"],
          "High Valid",              "H",
          "High Forecast",           "H",
          "High",                    "H",
          "Low Valid",               "L",
          "Low Forecast",            "L",
          "Low",                     "L",
          "",
        ],
        "text-size":              18,
        "text-font":              ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
        "text-anchor":            "center",
        "text-allow-overlap":     true,
        "text-ignore-placement":  true,
      },
      paint: {
        "text-color": [
          "match", ["get", "feat"],
          "High Valid",    "#3b82f6",
          "High Forecast", "#3b82f6",
          "High",          "#3b82f6",
          "Low Valid",     "#ef4444",
          "Low Forecast",  "#ef4444",
          "Low",           "#ef4444",
          "#94a3b8",
        ],
        "text-halo-color": "#0a101e",
        "text-halo-width": 2,
        "text-halo-blur":  0.5,
      },
    }, beforeId);
  }
}

export function setFrontsVisibility(map, visible) {
  const v = visible ? "visible" : "none";
  ["fronts-glow", "fronts-line-solid", "fronts-line-dashed", "fronts-symbols", "fronts-pressure-labels"].forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  });
}

// ── Range ring layer ────────────────────────────────────────────────────────

export function initRangeRingLayer(map) {
  if (!map.getSource("range-ring-source")) {
    map.addSource("range-ring-source", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getLayer("range-ring-layer")) {
    map.addLayer(
      {
        id:     "range-ring-layer",
        type:   "line",
        source: "range-ring-source",
        paint: {
          "line-color":     "#7ba7bc",
          "line-opacity":   0.42,
          "line-width":     1.2,
          "line-dasharray": [5, 4],
        },
      },
      "radar-sites-halo",
    );
  }
}

// ── Mesoscale Discussion layers ─────────────────────────────────────────────
// MD fill + outline are vector layers inserted above the radar raster tiles
// but below warning polygons, so storm-cell reflectivity shows through the
// semi-transparent fill while the solid outline remains readable on top.

const MD_COLOR_EXPR = [
  "match", ["get", "type"],
  "flood",  "#00c800",
  "winter", "#7878ff",
  /* default (severe / tornado-watch-likely) */ "#ff8c00",
];

export function initMdLayers(map) {
  if (!map.getSource("md-source")) {
    map.addSource("md-source", { type: "geojson", data: EMPTY_GEOJSON, promoteId: "id" });
  }
  // Insert before warnings-fill → renders above radar, below warning polygons.
  // Falls back to measure-line-glow (left map without warnings) or undefined
  // (right map, which has neither anchor) so addLayer never throws.
  const beforeId = map.getLayer("warnings-fill")     ? "warnings-fill"
                 : map.getLayer("measure-line-glow") ? "measure-line-glow"
                 : undefined;
  if (!map.getLayer("md-polygons")) {
    map.addLayer({
      id: "md-polygons", type: "fill", source: "md-source",
      paint: { "fill-color": MD_COLOR_EXPR, "fill-opacity": 0.22, "fill-antialias": true },
    }, beforeId);
  }
  if (!map.getLayer("md-outline")) {
    map.addLayer({
      id: "md-outline", type: "line", source: "md-source",
      paint: { "line-color": "rgba(255,255,255,0.65)", "line-width": 2 },
    }, beforeId);
  }
}

export function setMdVisibility(map, visible) {
  const v = visible ? "visible" : "none";
  ["md-polygons", "md-outline"].forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  });
}

// ── METAR overlay — station models ───────────────────────────────────────────
// Five layers per map: sky-cover circle, wind barb, and three text labels
// (temperature °F, dewpoint °F, SLP mb).  All layers share metar-source.
// 19 static icons are pre-built and selected via Mapbox expressions; wind barb
// icons are rotated per-feature by wdir so no per-station image is needed.
// minzoom: 5 on icon layers; minzoom: 7 on text labels.

// ── Icon factories ───────────────────────────────────────────────────────────

// fraction: 0=CLR, 0.25=FEW, 0.5=SCT, 0.75=BKN, 1.0=OVC
function _createSkyCoverIcon(fraction, color = "#000000") {
  const S = 40;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext("2d");
  const cx = S / 2, cy = S / 2, r = S / 2 - 3;
  // White fill
  ctx.fillStyle = "#ffffff";
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
  // Sky-cover slice (12-o'clock origin, clockwise sweep)
  if (fraction > 0) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + fraction * 2 * Math.PI);
    ctx.closePath();
    ctx.fill();
  }
  // Outline
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  return ctx.getImageData(0, 0, S, S);
}

// 80×80 canvas @ pixelRatio 2; station at center (40,40); staff points UP.
// Rotate the barb layer icon by wdir degrees on the map to orient correctly.
function _createWindBarbIcon(knotsRounded, color = "#000000") {
  const S = 80;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext("2d");
  const cx = S / 2, cy = S / 2;
  ctx.strokeStyle = color;
  ctx.fillStyle   = color;
  ctx.lineWidth   = 1.5;
  ctx.lineCap     = "round";

  if (knotsRounded === 0) {
    // Calm: two concentric circles
    ctx.beginPath(); ctx.arc(cx, cy, 8, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.stroke();
    return ctx.getImageData(0, 0, S, S);
  }

  // Staff: center → top of canvas
  const STAFF_TOP = 8;
  ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx, STAFF_TOP); ctx.stroke();

  // Barbs drawn from staff top downward, extending to the right of the staff
  const SPACING  = 8;
  const FULL_LEN = 18;
  const HALF_LEN = 9;
  let rem = knotsRounded;
  let y   = STAFF_TOP;

  while (rem >= 50) {                    // Pennant (50 kt) — filled triangle
    ctx.beginPath();
    ctx.moveTo(cx, y);
    ctx.lineTo(cx + FULL_LEN, y + SPACING / 2);
    ctx.lineTo(cx, y + SPACING);
    ctx.closePath(); ctx.fill();
    y += SPACING + 2; rem -= 50;
  }
  while (rem >= 10) {                    // Full barb (10 kt)
    ctx.beginPath();
    ctx.moveTo(cx, y);
    ctx.lineTo(cx + FULL_LEN, y + SPACING / 2);
    ctx.stroke();
    y += SPACING; rem -= 10;
  }
  if (rem >= 5) {                        // Half barb (5 kt)
    ctx.beginPath();
    ctx.moveTo(cx, y);
    ctx.lineTo(cx + HALF_LEN, y + SPACING / 4);
    ctx.stroke();
  }

  return ctx.getImageData(0, 0, S, S);
}

// Register all static METAR station-model icons once per map instance.
function _initMetarStationIcons(map, color = "#000000") {
  const SKY = [
    ["metar-sky-CLR", 0.00, "#000000"],
    ["metar-sky-FEW", 0.25, "#000000"],
    ["metar-sky-SCT", 0.50, "#000000"],
    ["metar-sky-BKN", 0.75, "#000000"],
    ["metar-sky-OVC", 1.00, "#000000"],
    ["metar-sky-UNK", 0.00, "#94a3b8"],  // gray outline = unknown/missing
  ];
  for (const [id, frac, color] of SKY) {
    if (!map.hasImage(id)) map.addImage(id, _createSkyCoverIcon(frac, color), { pixelRatio: 2 });
  }

  const BARB_SPEEDS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65];
  for (const kt of BARB_SPEEDS) {
    const id = kt === 0  ? "metar-wind-calm"
             : kt === 65 ? "metar-wind-65p"
             : `metar-wind-${kt}`;
    if (!map.hasImage(id)) map.addImage(id, _createWindBarbIcon(kt, color), { pixelRatio: 2 });
  }
}

// ── Enrichment helpers ───────────────────────────────────────────────────────

function _parseSkyCover(rawOb) {
  if (!rawOb) return "UNK";
  const RANK = { CLR: 0, SKC: 0, FEW: 1, SCT: 2, BKN: 3, OVC: 4, VV: 4 };
  const groups = rawOb.match(/\b(CLR|SKC|FEW|SCT|BKN|OVC|VV)\d{0,3}\b/g) ?? [];
  let worst = -1, result = "UNK";
  for (const g of groups) {
    const code = g.replace(/\d+$/, "");
    const rank = RANK[code] ?? -1;
    if (rank > worst) {
      worst  = rank;
      result = code === "VV" ? "OVC" : code === "SKC" ? "CLR" : code;
    }
  }
  return worst === -1 ? "CLR" : result;
}

function _getWindBin(wspd, wdir) {
  if (wdir === "VRB" || !wspd || wspd < 3) return "calm";
  const rounded = Math.round(wspd / 5) * 5;
  if (rounded >= 65) return "65p";
  return String(Math.max(5, rounded));  // 3–7 kt → bin "5"
}

// Adds derived display properties to each feature in-place, returns the FC.
// Call on fetched GeoJSON before passing to setMetarData().
export function enrichMetarData(fc) {
  if (!fc?.features) return fc;
  for (const f of fc.features) {
    const p = f.properties;
    p.skyCover = _parseSkyCover(p.rawOb);
    p.windBin  = _getWindBin(p.wspd, p.wdir);
    p.wdirNum  = typeof p.wdir  === "number" ? p.wdir  : 0;
    p.tempF    = typeof p.temp  === "number" ? Math.round(p.temp  * 9 / 5 + 32)   : null;
    p.dewpF    = typeof p.dewp  === "number" ? Math.round(p.dewp  * 9 / 5 + 32)   : null;
    p.slp      = typeof p.altim === "number" ? Math.round(p.altim * 33.8639)       : null;
  }
  return fc;
}

// ── Layer init ───────────────────────────────────────────────────────────────

export function initMetarLayers(map, visible = false, color = "#000000") {
  _initMetarStationIcons(map, color);

  if (!map.getSource("metar-source")) {
    map.addSource("metar-source", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  // Insert all METAR layers below radar-site markers.
  const beforeId = map.getLayer("radar-sites-halo")  ? "radar-sites-halo"
                 : map.getLayer("radar-sites-layer")  ? "radar-sites-layer"
                 : undefined;

  const iconSizeExpr = (lo, hi) => [
    "interpolate", ["linear"], ["zoom"],
    5, lo, 9, (lo + hi) / 2, 12, hi,
  ];

  const TEXT_PAINT = {
    "text-color":      "#000000",
    "text-halo-color": "#ffffff",
    "text-halo-width": 1.5,
  };

  if (!map.getLayer("metar-sky-layer")) {
    map.addLayer({
      id:      "metar-sky-layer",
      type:    "symbol",
      source:  "metar-source",
      minzoom: 5,
      layout: {
        "icon-image": ["concat", "metar-sky-", ["get", "skyCover"]],
        "icon-size":  iconSizeExpr(0.7, 1.2),
      },
    }, beforeId);
  }

  if (!map.getLayer("metar-wind-layer")) {
    map.addLayer({
      id:      "metar-wind-layer",
      type:    "symbol",
      source:  "metar-source",
      minzoom: 5,
      layout: {
        "icon-image":              ["concat", "metar-wind-", ["get", "windBin"]],
        "icon-size":               iconSizeExpr(0.65, 1.0),
        "icon-rotate":             ["coalesce", ["get", "wdirNum"], 0],
        "icon-rotation-alignment": "map",
        "icon-pitch-alignment":    "map",
      },
    }, beforeId);
  }

  if (!map.getLayer("metar-temp-label")) {
    map.addLayer({
      id:      "metar-temp-label",
      type:    "symbol",
      source:  "metar-source",
      minzoom: 7,
      layout: {
        "text-field":  ["coalesce", ["to-string", ["get", "tempF"]], ""],
        "text-font":   ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":   11,
        "text-offset": [-1.4, -0.9],
        "text-anchor": "center",
      },
      paint: TEXT_PAINT,
    }, beforeId);
  }

  if (!map.getLayer("metar-dewp-label")) {
    map.addLayer({
      id:      "metar-dewp-label",
      type:    "symbol",
      source:  "metar-source",
      minzoom: 7,
      layout: {
        "text-field":  ["coalesce", ["to-string", ["get", "dewpF"]], ""],
        "text-font":   ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":   11,
        "text-offset": [-1.4, 0.9],
        "text-anchor": "center",
      },
      paint: TEXT_PAINT,
    }, beforeId);
  }

  if (!map.getLayer("metar-altim-label")) {
    map.addLayer({
      id:      "metar-altim-label",
      type:    "symbol",
      source:  "metar-source",
      minzoom: 7,
      layout: {
        "text-field":  ["coalesce", ["to-string", ["get", "slp"]], ""],
        "text-font":   ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":   11,
        "text-offset": [1.4, -0.9],
        "text-anchor": "center",
      },
      paint: TEXT_PAINT,
    }, beforeId);
  }

  setMetarVisibility(map, visible);
}

export function setMetarVisibility(map, visible) {
  const v = visible ? "visible" : "none";
  for (const id of [
    "metar-sky-layer", "metar-wind-layer",
    "metar-temp-label", "metar-dewp-label", "metar-altim-label",
  ]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  }
}

// ── Spotter Network layers ────────────────────────────────────────────────────
// GeoJSON source with clustering.  Individual dots are colored by status;
// verified spotters get a thicker white stroke.  Labels appear at z10+.

export function initSpotterLayers(map, visible = false) {
  const beforeId = map.getLayer("radar-sites-halo") ? "radar-sites-halo"
                 : map.getLayer("radar-sites-layer") ? "radar-sites-layer"
                 : undefined;

  if (!map.getSource("spotter-source")) {
    map.addSource("spotter-source", {
      type:          "geojson",
      data:          { type: "FeatureCollection", features: [] },
      cluster:       true,
      clusterMaxZoom: 12,
      clusterRadius:  40,
    });
  }
  if (!map.getLayer("spotter-clusters")) {
    map.addLayer({
      id:     "spotter-clusters",
      type:   "circle",
      source: "spotter-source",
      filter: ["has", "point_count"],
      paint: {
        "circle-color":        "#4cc9ff",
        "circle-radius":       ["step", ["get", "point_count"], 14, 5, 18, 10, 22],
        "circle-opacity":      0.85,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#ffffff",
      },
    }, beforeId);
  }
  if (!map.getLayer("spotter-cluster-count")) {
    map.addLayer({
      id:     "spotter-cluster-count",
      type:   "symbol",
      source: "spotter-source",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font":  ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":  11,
      },
      paint: { "text-color": "#ffffff" },
    }, beforeId);
  }
  if (!map.getLayer("spotter-points")) {
    map.addLayer({
      id:     "spotter-points",
      type:   "circle",
      source: "spotter-source",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color":        ["match", ["get", "status"], "Chasing", "#4cc9ff", "#94a3b8"],
        "circle-radius":       7,
        "circle-stroke-width": ["case", ["==", ["get", "verified"], 1], 2, 1],
        "circle-stroke-color": "#ffffff",
      },
    }, beforeId);
  }
  if (!map.getLayer("spotter-labels")) {
    map.addLayer({
      id:      "spotter-labels",
      type:    "symbol",
      source:  "spotter-source",
      filter:  ["!", ["has", "point_count"]],
      minzoom: 10,
      layout: {
        "text-field":  ["get", "name"],
        "text-font":   ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":   11,
        "text-offset": [0, -1.4],
        "text-anchor": "bottom",
      },
      paint: {
        "text-color":      "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
    }, beforeId);
  }
  setSpotterVisibility(map, visible);
}

export function setSpotterVisibility(map, visible) {
  const v = visible ? "visible" : "none";
  for (const id of ["spotter-clusters", "spotter-cluster-count", "spotter-points", "spotter-labels"]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  }
}

// ── Storm Report layers ───────────────────────────────────────────────────────
// No clustering — individual report events should always be visible.
// Color is pre-computed by reportsToGeoJson() from REPORT_CONFIG.

export function initReportLayers(map, visible = false) {
  const beforeId = map.getLayer("radar-sites-halo") ? "radar-sites-halo"
                 : map.getLayer("radar-sites-layer") ? "radar-sites-layer"
                 : undefined;

  if (!map.getSource("report-source")) {
    map.addSource("report-source", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getLayer("report-points")) {
    map.addLayer({
      id:     "report-points",
      type:   "circle",
      source: "report-source",
      paint: {
        "circle-color":          ["get", "color"],
        "circle-radius":         ["get", "sourceRadius"],
        "circle-stroke-width":   ["get", "sourceStrokeWidth"],
        "circle-stroke-color":   ["get", "sourceStrokeColor"],
        "circle-opacity":        ["get", "opacity"],
        "circle-stroke-opacity": ["get", "opacity"],
      },
    }, beforeId);
  }
  if (!map.getLayer("report-labels")) {
    map.addLayer({
      id:     "report-labels",
      type:   "symbol",
      source: "report-source",
      layout: {
        "text-field": ["get", "abbr"],
        "text-font":  ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size":  9,
      },
      paint: { "text-color": "#ffffff" },
    }, beforeId);
  }
  setReportVisibility(map, visible);
}

export function setReportVisibility(map, visible) {
  const v = visible ? "visible" : "none";
  for (const id of ["report-points", "report-labels"]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  }
}

export function setReportFilter(map, enabledSources, enabledTypes) {
  const srcFilter  = enabledSources.length
    ? ["in", ["get", "source"], ["literal", enabledSources]]
    : ["boolean", false];
  const typeFilter = enabledTypes.length
    ? ["in", ["get", "type"], ["literal", enabledTypes]]
    : ["boolean", false];
  const combined = ["all", srcFilter, typeFilter];
  for (const id of ["report-points", "report-labels"]) {
    if (map.getLayer(id)) map.setFilter(id, combined);
  }
}


